import React, { useState } from 'react'
import { Formik } from 'formik'
import { get } from 'lodash/fp'
import { gql } from '@apollo/client'

import { Spinner, ErrorMessage } from '../../../Elements'
import { JobSchema } from '@mmg/validation'
import JobForm from '../JobForm'
import { useNavigate } from 'react-router-dom'
import { useToast } from '../../../Hooks/toast'

import { DEFAULT_JOB_STATE } from '../../../utils/state'
import { useOfflineQuery, useOfflineMutation } from '../../../Hooks/graphql_utils'

import { getTempLatLon, shouldGetTemporaryLatLon } from '../JobForm/helpers'

const LOOKUP_QUERY = gql`
    query jobNewLookupQuery {
        lookupJobType {
            id
            name
            show_gunite
            show_pebble
            show_warranty
        }
        lookupJobCategory {
            id
            name
        }
        lookupPoolType {
            id
            name
        }
        lookupSpaType {
            id
            name
        }
        lookupJobStatus {
            id
            name
            show_gunite
            show_pebble
            show_warranty
            show_pebble_prep
            show_pebble_application
            show_pebble_detail
            show_pebble_slurry
        }
        customers: lookupCustomer {
            id
            company_name
            is_active
        }
        customer_contacts {
            id
            first_name
            last_name
            phone_number
            customer_id
        }
        homeowners {
            id
            first_name
            last_name
            phone_number
        }
        lookupPebbleColor {
            id
            name
            material_type {
                id
                name
            }
            pool_code
            spa_code
            price_level
            condition
            batch_ratio
        }
        lookupPebbleColorAdditive {
            id
            name
            quantity_per_batch
            quantity_per_job
            additive_product_id
        }
        lookupPebbleColorMaterialType {
            id
            name
        }       
        lookupPoolSection {
            id
            name
            additional_batches
            is_add_pool_batches
        }
        lookupCrewTypes {
            id
            name
            lookup_company_division_id
            subdivision
            is_active
        }
        lookupGuniteHistoryTypes {
            id
            name
        }
        lookupJobOrder {
            id
            name
        }
        lookupAddition {
            id
            name
            type
        }
        lookupEmployee {
            id
            name
            is_active
            crew_types {
                id
            }
        }
        lookupRepairSupervisor {
            id
            name
            crew_types {
                id
            }
        }
        jobDescriptionJobType{
                job_type {
                    id
                    name
                }
                job_description {
                    id
                    name
                }
            }
        lookupJobPriority {
            id
            name
        }
        lookupWarrantyCategory {
            id
            name
        }
        lookupSlurryType {
                id
                name
                type
            }
        lookupVgbMainDrainsBy{
                id
                name
            }
        lookupPebblePhaseType {
            id
            name
        }
        presiteSupervisors{
           id
           company_division_id
        }
        lookupCompanyDivisions {
            id
            name
        }
        lookupSlurryPickup {
            id
            name
            is_active
        }
    }   
`

const MUTATION = gql`
mutation CreateJob($job: JobInput!) {
    createJob(job: $job) {
        id
    }
}
`

export default () => {
    const navigate = useNavigate()
    const { showSuccess, showError } = useToast()
    const [createJob] = useOfflineMutation(MUTATION)
    // eslint-disable-next-line
    const [trackedInitialValues, setTrackedInitialValues] = useState(null)

    const { loading, error, data } = useOfflineQuery(LOOKUP_QUERY, {
    })

    if (loading) {
        return <Spinner />
    }

    if (error) {
        return <ErrorMessage />
    }

    const onSubJobDelete = async (props, { setValues }) => {
        props.job.sub_jobs.splice(props.job_detail_index, 1)
        const jobMinusDeletedDetail = { ...props.job, sub_jobs: [...props.job.sub_jobs] }
        setValues(jobMinusDeletedDetail)
        setTrackedInitialValues(jobMinusDeletedDetail)
    }

    const onSubmit = async (props) => {
        let model = JobSchema.cast(props, { stripUnknown: true })

        //remove jobDetails for which the user has not selected a category.
        model.sub_jobs = model.sub_jobs?.filter(sj => sj.job_category?.id)

        //Get temporary lat and lon if applicable
        if (shouldGetTemporaryLatLon(model)) {
            model = await getTempLatLon(model)
        }

        await createJob({
            variables: {
                job: model
            }
        })
            .then(res => {
                showSuccess('Job saved!')
                const id = get('data.createJob.id')(res)
                return id
            })
            .then(id => {
                navigate(`/job/${id}/edit`, { replace: true, state: { fromJobCreationPage: true } })
            })
            .catch(() => showError('Error, please try again.'))
    }

    const onReset = () => navigate('/job')
    return (
        <Formik
            initialValues={DEFAULT_JOB_STATE}
            onSubmit={onSubmit}
            onReset={onReset}
            render={props =>
                <JobForm
                    lookupOptions={data}
                    customers={data.customers}
                    supervisors={data.customer_contacts}
                    homeowners={data.homeowners}
                    onCancel={onReset}
                    onSubJobDelete={onSubJobDelete}
                    {...props}
                />
            }
            validationSchema={JobSchema}
        />
    )
}